import React from 'react';
import { Pressable, View } from 'react-native';
import { discounted, fmt, selectActiveCart, useAppStore } from '@restorant/core';
import type { MenuItem } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useCatLabel, useT } from '../i18n';
import { useDiscountPresets, useItemName, useSettings } from '../menu';
import { usePrompt } from '../prompt';
import { Icon } from '../icons/Icon';
import { T } from './Text';
import { MiniButton } from './MiniButton';

/**
 * `order` — price chips add to the open table's order.
 * `manage` — prices are read-only and the offer controls are shown instead.
 */
export type ItemCardMode = 'order' | 'manage';

function PriceChip({
  label,
  price,
  offer,
  interactive,
  onPress,
}: {
  label: string;
  price: number;
  offer: number;
  interactive: boolean;
  onPress: () => void;
}) {
  const colors = useColors();
  const now = offer > 0 ? discounted(price, offer) : price;

  const body = (
    <>
      <T size={10.5} color={colors.textFaint} weight={600}>
        {label}
      </T>
      {offer > 0 ? (
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 5 }}>
          <T
            size={11}
            color={colors.textFaint}
            weight={500}
            style={{ textDecorationLine: 'line-through' }}
          >
            {fmt(price)}
          </T>
          <T size={13.5} weight={700} color={colors.gold}>
            {fmt(now)}
          </T>
        </View>
      ) : (
        <T size={13.5} weight={700}>
          {fmt(price)}
        </T>
      )}
    </>
  );

  const boxStyle = {
    backgroundColor: colors.surface2,
    borderWidth: 1,
    borderRadius: 9,
    paddingVertical: 7,
    paddingHorizontal: 10,
    minWidth: 76,
    gap: 2,
  };

  if (!interactive) {
    return <View style={[boxStyle, { borderColor: colors.line }]}>{body}</View>;
  }

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: any) => [
        boxStyle,
        {
          borderColor: hovered || pressed ? colors.gold : colors.line,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        },
      ]}
    >
      <View style={{ gap: 2 }}>{body}</View>
      <Icon name="plus" size={14} color={colors.gold} />
    </Pressable>
  );
}

export function ItemCard({ item, mode = 'manage' }: { item: MenuItem; mode?: ItemCardMode }) {
  const colors = useColors();
  const t = useT();
  const catLabel = useCatLabel();
  const itemName = useItemName();
  const presets = useDiscountPresets();
  const settings = useSettings();
  const displayName = itemName(item);
  const offer = useAppStore((s) => s.offers[item.id] ?? 0);
  // Units sold, counted straight off the order history.
  const sold = useAppStore((s) =>
    s.orders.reduce(
      (n, o) => n + o.items.reduce((m, l) => (l.itemId === item.id ? m + l.qty : m), 0),
      0,
    ),
  );
  const addToCart = useAppStore((s) => s.addToCart);
  const setItemOffer = useAppStore((s) => s.setItemOffer);
  const openPrompt = usePrompt();

  // How many of this item are already on the open table.
  const inCart = useAppStore((s) =>
    selectActiveCart(s).reduce((n, l) => (l.itemId === item.id ? n + l.qty : n), 0),
  );

  const ordering = mode === 'order';

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: inCart > 0 ? colors.goldDim : colors.line,
        borderRadius: radius.lg,
        padding: 14,
        gap: 11,
      }}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
        <View style={{ flexShrink: 1, gap: 3 }}>
          {!ordering ? (
            <T size={10} weight={600} color={colors.textFaint} style={{ letterSpacing: 0.6 }}>
              {catLabel(item.cat).toUpperCase()}
            </T>
          ) : null}
          <T size={14.5} weight={700} style={{ lineHeight: 19 }}>
            {displayName}
          </T>
        </View>

        <View style={{ gap: 5, alignItems: 'flex-end' }}>
          {offer > 0 ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                backgroundColor: colors.spice,
                borderRadius: radius.pill,
                paddingVertical: 3,
                paddingHorizontal: 8,
              }}
            >
              <Icon name="percent" size={11} color="#FFFFFF" />
              <T size={10} weight={800} color="#FFFFFF">
                {offer}% {t('off')}
              </T>
            </View>
          ) : null}

          {inCart > 0 ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                backgroundColor: colors.gold,
                borderRadius: radius.pill,
                paddingVertical: 3,
                paddingHorizontal: 8,
              }}
            >
              <Icon name="check" size={11} color={colors.onGold} />
              <T size={10} weight={800} color={colors.onGold}>
                {inCart}
              </T>
            </View>
          ) : null}

          {!ordering && sold > 0 ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                backgroundColor: colors.surface2,
                borderWidth: 1,
                borderColor: colors.goldDim,
                borderRadius: radius.pill,
                paddingVertical: 2,
                paddingHorizontal: 7,
              }}
            >
              <Icon name="flame" size={11} color={colors.gold} />
              <T size={10} weight={700} color={colors.gold}>
                {sold}
              </T>
            </View>
          ) : null}
        </View>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {Object.entries(item.prices).map(([label, price]) => (
          <PriceChip
            key={label}
            label={label}
            price={price}
            offer={offer}
            interactive={ordering}
            onPress={() => addToCart(item.id, label)}
          />
        ))}
      </View>

      {!ordering ? (
        <View
          style={{
            flexDirection: 'row',
            gap: 6,
            borderTopWidth: 1,
            borderTopColor: colors.line,
            paddingTop: 11,
          }}
        >
          {presets.map((pct) => (
            <MiniButton
              key={pct}
              label={`${pct}%`}
              active={offer === pct}
              onPress={() => setItemOffer(item.id, pct)}
            />
          ))}
          <MiniButton
            label={t('custom')}
            onPress={() =>
              openPrompt({
                title: t('custom_discount_for', { name: displayName }),
                hint: t('discount_range_hint', { max: settings.maxDiscountPct }),
                initial: offer ? String(offer) : '',
                mode: 'percent',
                onSubmit: (v) => setItemOffer(item.id, Number(v)),
              })
            }
          />
          {offer > 0 ? (
            <MiniButton label={t('clear')} onPress={() => setItemOffer(item.id, 0)} />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
